import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useModalStore } from "@/store/use-modal-store";
import type { ActiveCoupon } from "@/api/models/api-types";
import { cn } from "@/lib/utils";
import {
  trackMonetizationPopupClosed,
  trackMonetizationPopupUpgradeClicked,
  trackMonetizationPopupViewed,
  type MonetizationSource,
} from "@/lib/monetization-analytics";
import discountIcon from "@/assets/discount-icon.png";
import type { PaymentRequiredCode } from "@/api/middleware/payment-required-middleware";

/**
 * 402'nin sebebine göre başlık ve açıklama.
 *
 * Sebep ayırt edilemiyorsa (kod yok) eski genel metinler korunur. Günlük limiti
 * dolan kullanıcıya "krediniz tükendi" demek yanlış yönlendirmeydi: bakiyesi
 * duruyor, yalnızca limitin yenilenmesini beklemesi gerekiyor. Başlık ve gövde
 * birlikte seçiliyor — yalnız gövdeyi değiştirmek, başlığın gövdeyle çelişmesine
 * yol açıyordu.
 */
const copyFor = (
  code: PaymentRequiredCode | undefined,
): { titleKey: string; descriptionKey: string } => {
  switch (code) {
    case "PAYMENT_REQUIRED":
      return {
        titleKey: "modal.paymentRequiredTitle",
        descriptionKey: "chat.errors.paymentRequired",
      };
    case "DAILY_LIMIT_REACHED":
      return {
        titleKey: "modal.dailyLimitReachedTitle",
        descriptionKey: "chat.errors.dailyLimitReached",
      };
    case "ACCOUNT_UNAVAILABLE":
      return {
        titleKey: "modal.accountUnavailableTitle",
        descriptionKey: "chat.errors.accountUnavailable",
      };
    default:
      return {
        titleKey: "modal.paymentRequiredTitle",
        descriptionKey: "modal.paymentRequiredDescription",
      };
  }
};

export const PaymentRequiredModal = () => {
  const { t } = useTranslation();
  const { isOpen, type, data, onClose } = useModalStore();
  const [copied, setCopied] = useState(false);

  const isVisible = isOpen && type === "paymentRequired";
  const coupon = data?.coupon as ActiveCoupon | null | undefined;
  const source =
    (data?.monetizationSource as MonetizationSource | undefined) ?? "other";
  const paymentCode = data?.paymentCode as PaymentRequiredCode | undefined;
  const paymentRequestId = data?.paymentRequestId;
  const copy = copyFor(paymentCode);

  // Modal, kupon bilgisi geldiğinde ikinci kez "açılıyor" (aynı görünüm zenginleşiyor).
  // View event'i modal açıldığında veya yeni request açık modalın yerini
  // aldığında, sonuç event'i ise request yaşam döngüsünde bir kez atılır.
  const viewTracked = useRef(false);
  const outcomeTracked = useRef(false);
  const trackedRequestId = useRef<string | undefined>(undefined);
  const trackedSource = useRef<MonetizationSource>(source);

  useEffect(() => {
    if (!isVisible) {
      viewTracked.current = false;
      trackedRequestId.current = undefined;
      setCopied(false);
      return;
    }
    if (
      viewTracked.current &&
      trackedRequestId.current === paymentRequestId
    ) {
      return;
    }

    // Açık modal yeni bir 402 isteğiyle yer değiştirirse eski görünümü kapatılmış
    // say, ardından yeni isteğin analytics yaşam döngüsünü bağımsız başlat.
    if (viewTracked.current && !outcomeTracked.current) {
      trackMonetizationPopupClosed(trackedSource.current);
    }

    viewTracked.current = true;
    outcomeTracked.current = false;
    trackedRequestId.current = paymentRequestId;
    trackedSource.current = source;
    setCopied(false);
    trackMonetizationPopupViewed(source);
  }, [isVisible, paymentRequestId, source]);

  const trackOutcome = (track: (source: MonetizationSource) => void) => {
    if (outcomeTracked.current) return;
    outcomeTracked.current = true;
    track(trackedSource.current);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      trackOutcome(trackMonetizationPopupClosed);
      onClose();
    }
  };

  const handleConfirm = () => {
    trackOutcome(trackMonetizationPopupUpgradeClicked);
    onClose();
    if (data?.onConfirm) data.onConfirm();
  };

  const handleCancel = () => {
    trackOutcome(trackMonetizationPopupClosed);
    onClose();
    if (data?.onCancel) data.onCancel();
  };

  const handleCopyCode = async () => {
    if (!coupon?.code) return;
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — silently ignore.
    }
  };

  return (
    <Dialog open={isVisible} onOpenChange={handleOpenChange}>
      <DialogContent
        open={isVisible}
        className={cn(
          "p-0 gap-0 overflow-hidden",
          "max-w-[400px] w-[calc(100vw-2rem)]",
          "bg-white dark:bg-gray-900",
          "border-0 rounded-3xl shadow-2xl",
        )}
      >
        {/* Dekoratif gradient üst şerit — marka renkleri */}
        <div className="absolute inset-x-0 top-0 h-1 rounded-t-3xl bg-gradient-to-r from-[#39BFB8] via-[#B34B8D] to-[#672D83]" />

        {/* İçerik */}
        <div className="px-6 pt-8 pb-6 flex flex-col items-center text-center gap-4">
          {/* İkon alanı — hediye kutusu */}
          <div className="relative flex items-center justify-center w-16 h-16">
            <div className="absolute inset-0 rounded-full bg-[#672D83]/20 blur-lg" />
            <div className="relative z-10 flex items-center justify-center w-14 h-14 rounded-full bg-[#B34B8D]/10 dark:bg-[#672D83]/30 text-3xl">
              🎁
            </div>
          </div>

          {/* Başlık */}
          <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-snug">
            {coupon?.title || t(copy.titleKey)}
          </h2>

          {/* Açıklama */}
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs">
            {String(data?.message ?? t(copy.descriptionKey))}
          </p>

          {/* Kampanya kutusu — yalnızca kullanıcı bir kupona uygunsa göster */}
          {coupon && (
            <div className="w-full flex flex-col gap-3 rounded-2xl bg-gradient-to-r from-[#39BFB8]/10 to-[#672D83]/10 dark:from-[#39BFB8]/15 dark:to-[#672D83]/20 border border-[#B34B8D]/30 dark:border-[#B34B8D]/40 px-4 py-3">
              <div className="flex items-start gap-3">
                <img
                  src={discountIcon}
                  alt=""
                  className="w-6 h-6 mt-0.5 shrink-0"
                />
                <div className="text-left">
                  <p className="text-sm text-[#672D83] dark:text-[#39BFB8] font-semibold leading-snug">
                    {t("modal.couponUrgency")}
                  </p>
                  {coupon.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 font-medium leading-snug mt-0.5">
                      {coupon.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Kupon kodu */}
              <button
                type="button"
                onClick={handleCopyCode}
                className={cn(
                  "w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2",
                  "bg-white/70 dark:bg-gray-900/40",
                  "border border-dashed border-[#B34B8D]/50 dark:border-[#B34B8D]/60",
                  "transition-colors hover:bg-white dark:hover:bg-gray-900/70",
                )}
              >
                <span className="font-mono font-bold tracking-wider text-[#672D83] dark:text-[#39BFB8]">
                  {coupon.code}
                </span>
                <span className="text-xs font-semibold text-[#39BFB8]">
                  {copied ? t("modal.couponCopied") : t("modal.couponCopy")}
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Butonlar */}
        <div className="px-6 pb-6 flex flex-col gap-2.5">
          {data?.onConfirm && (
            <button
              onClick={handleConfirm}
              className={cn(
                "w-full py-3 rounded-2xl text-sm font-semibold transition-all duration-150",
                "bg-gradient-to-r from-[#B34B8D] to-[#672D83]",
                "hover:opacity-90",
                "active:scale-[0.98]",
                "text-white shadow-md",
              )}
            >
              {t("modal.buyNow")}
            </button>
          )}
          <button
            onClick={handleCancel}
            className={cn(
              "w-full py-3 rounded-2xl text-sm font-semibold transition-all duration-150",
              "bg-gray-100 dark:bg-gray-800",
              "hover:bg-gray-200 dark:hover:bg-gray-700",
              "active:scale-[0.98]",
              "text-gray-700 dark:text-gray-300",
            )}
          >
            {t("modal.cancel")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
